import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import SchoolIcon from '@mui/icons-material/School';
import ConfirmDialog from '../../../../dashboard/components/ConfirmDialog';
import { getCourses, deleteCourse } from '../../../../api/TrainerAPI';
import './CoursesTab.css';

const CoursesTab = ({ trainerId }) => {
    const navigate = useNavigate();
    const [courses, setCourses] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);

    useEffect(() => {
        if (trainerId) {
            loadCourses();
        }
    }, [trainerId]);

    const loadCourses = async () => {
        setIsLoading(true);
        setError('');
        try {
            const coursesData = await getCourses(trainerId);
            setCourses(coursesData || []);
        } catch (err) {
            setError('Failed to load courses');
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    };

    const handleCreateCourse = () => {
        navigate(`/connect/console/${trainerId}/course/new`);
    };

    const handleEditCourse = (courseId) => {
        navigate(`/connect/console/${trainerId}/course/${courseId}`);
    };

    const handleDeleteClick = (e, course) => {
        e.stopPropagation();
        setDeleteConfirm(course);
    };

    const handleConfirmDelete = async () => {
        if (!deleteConfirm) return;
        
        setIsDeleting(true);
        try {
            await deleteCourse(trainerId, deleteConfirm.id);
            setCourses(prev => prev.filter(c => c.id !== deleteConfirm.id));
            setDeleteConfirm(null);
        } catch (err) {
            console.error('Failed to delete course:', err);
        } finally {
            setIsDeleting(false);
        }
    };

    const handleCancelDelete = () => {
        setDeleteConfirm(null);
    };

    if (isLoading) {
        return (
            <div className="courses-tab">
                <div className="courses-loading">
                    <div className="courses-loading__spinner"></div>
                    <p>Loading courses...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="courses-tab">
                <div className="courses-error">
                    <p>{error}</p>
                    <button onClick={loadCourses}>Try Again</button>
                </div>
            </div>
        );
    }

    return (
        <div className="courses-tab">
            {/* Create Course Button */}
            <div className="courses-tab__header">
                <button className="courses-create-btn" onClick={handleCreateCourse}>
                    <AddIcon sx={{ fontSize: 18 }} />
                    Create Course
                </button>
            </div>

            {/* Courses List */}
            {courses.length === 0 ? (
                <div className="courses-empty">
                    <MenuBookIcon sx={{ fontSize: 48 }} />
                    <p>No courses yet. Create your first course!</p>
                </div>
            ) : (
                <div className="courses-grid">
                    {courses.map(course => (
                        <div 
                            key={course.id} 
                            className="course-card-item"
                            onClick={() => handleEditCourse(course.id)}
                        >
                            <div className="course-card-item__icon">
                                <SchoolIcon sx={{ fontSize: 32 }} />
                            </div>
                            <div className="course-card-item__content">
                                <h3 className="course-card-item__title">{course.title}</h3>
                                {course.description && (
                                    <p className="course-card-item__desc">{course.description}</p>
                                )}
                                <div className="course-card-item__meta">
                                    <span>{course.modules?.length || 0} modules</span>
                                </div>
                            </div>
                            <button 
                                className="course-card-item__delete"
                                onClick={(e) => handleDeleteClick(e, course)}
                                title="Delete Course"
                            >
                                <DeleteIcon sx={{ fontSize: 18 }} />
                            </button>
                        </div>
                    ))}
                </div>
            )}

            {/* Delete Confirmation Dialog */}
            {deleteConfirm && (
                <ConfirmDialog
                    props={{
                        message: `Are you sure you want to delete "${deleteConfirm.title}"? This action cannot be undone.`
                    }}
                    onConfirm={handleConfirmDelete}
                    onCancel={handleCancelDelete}
                />
            )}
        </div>
    );
};

export default CoursesTab;
